import React from 'react';
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import { OutlinedBadge } from '@tms/shared-ui';
import type { UserSummary } from '../../services/adminAnalyticsApi';
import { formatDurationMs, formatMoney } from '../../services/adminAnalyticsApi';

type Props = {
  summary: UserSummary;
  onOpenTab: (tab: string) => void;
  onExport?: () => void;
  exportLoading?: boolean;
};

const SummaryCard: React.FC<{
  title: string;
  children: React.ReactNode;
  action?: { label: string; tab: string };
  onOpenTab?: (tab: string) => void;
}> = ({ title, children, action, onOpenTab }) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
      <Typography variant="subtitle2" color="primary" fontWeight={600}>
        {title}
      </Typography>
      {action && onOpenTab ? (
        <Button size="small" onClick={() => onOpenTab(action.tab)}>
          {action.label}
        </Button>
      ) : null}
    </Box>
    {children}
  </Paper>
);

const subscriptionColor = (state: string): 'success' | 'warning' | 'error' | 'default' => {
  if (state === 'paid') return 'success';
  if (state === 'trial' || state === 'grace') return 'warning';
  if (state === 'canceled' || state === 'expired') return 'error';
  return 'default';
};

const riskChipColor = (level: string): 'success' | 'warning' | 'error' => {
  if (level === 'high') return 'error';
  if (level === 'medium') return 'warning';
  return 'success';
};

const formatDate = (iso?: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const UserOverviewPanel: React.FC<Props> = ({ summary, onOpenTab, onExport, exportLoading }) => {
  const { identity, engagement, subscription, progression, teacher, risk } = summary;

  return (
    <Box>
      {onExport ? (
        <Box display="flex" justifyContent="flex-end" mb={1}>
          <Button size="small" variant="outlined" onClick={onExport} disabled={exportLoading}>
            {exportLoading ? 'Exporting…' : 'Export user data (JSON)'}
          </Button>
        </Box>
      ) : null}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} lg={4}>
          <SummaryCard title="Identity" action={{ label: 'Edit', tab: 'profile' }} onOpenTab={onOpenTab}>
            <Typography variant="body1" fontWeight={600}>
              {identity.displayName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {identity.email}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {identity.role}
              {identity.isTeacher && identity.role !== 'Teacher' ? ' (also teaches)' : ''} ·{' '}
              {identity.status}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Registered {formatDate(identity.registeredAt)}
            </Typography>
            {identity.city ? (
              <Typography variant="body2" color="text.secondary">
                {identity.city}
                {identity.postalCode ? ` (${identity.postalCode})` : ''}
              </Typography>
            ) : null}
          </SummaryCard>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <SummaryCard title="Engagement" action={{ label: 'Activity', tab: 'activity' }} onOpenTab={onOpenTab}>
            <Typography variant="body2">
              Last activity: <strong>{formatDate(engagement.lastActivityAt)}</strong>
            </Typography>
            <Typography variant="body2">
              Active days (30d): <strong>{engagement.activeDaysLast30}</strong>
            </Typography>
            <Typography variant="body2">
              Sessions: <strong>{engagement.totalSessions}</strong>
              {engagement.totalSessions > 0
                ? ` · avg ${formatDurationMs(engagement.averageSessionDurationMs)}`
                : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Course sessions: {engagement.courseSessionsCount}
            </Typography>
          </SummaryCard>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <SummaryCard
            title="Subscription"
            action={{ label: 'Manage', tab: 'subscription' }}
            onOpenTab={onOpenTab}
          >
            <Box sx={{ mb: 1 }}>
              <OutlinedBadge
                label={subscription.state}
                color={subscriptionColor(subscription.state)}
                size="small"
              />
            </Box>
            {subscription.planName ? (
              <Typography variant="body2">{subscription.planName}</Typography>
            ) : null}
            <Typography variant="body2" color="text.secondary">
              {subscription.isTrial && subscription.trialEndDate
                ? `Trial ends ${formatDate(subscription.trialEndDate)}`
                : subscription.endDate
                  ? `Ends ${formatDate(subscription.endDate)}`
                  : 'No active plan'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Total paid:{' '}
              <strong>{formatMoney(subscription.totalPaid, subscription.currency)}</strong>
            </Typography>
          </SummaryCard>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <SummaryCard
            title="Learning"
            action={{ label: 'Progression', tab: 'progression' }}
            onOpenTab={onOpenTab}
          >
            <Typography variant="body2">
              Level <strong>{progression.currentLevel}</strong> · XP{' '}
              <strong>{progression.totalExperience}</strong>
            </Typography>
            <Typography variant="body2">
              Documents: <strong>{progression.completedDocuments}</strong> completed /{' '}
              {progression.inProgressDocuments} in progress
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Badges: {progression.totalBadges}
            </Typography>
          </SummaryCard>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <SummaryCard title="Teacher" action={{ label: 'Relations', tab: 'relations' }} onOpenTab={onOpenTab}>
            {teacher.isTeaching || identity.role === 'Teacher' || identity.isTeacher ? (
              <>
                <Typography variant="body2">
                  Students assigned: <strong>{teacher.studentCount ?? 0}</strong>
                </Typography>
                {teacher.assigned ? (
                  <Typography variant="body2" color="text.secondary">
                    Own teacher: <strong>{teacher.teacherName}</strong>
                  </Typography>
                ) : null}
              </>
            ) : teacher.assigned ? (
              <Typography variant="body2">
                Assigned to <strong>{teacher.teacherName}</strong>
              </Typography>
            ) : (
              <Typography variant="body2" color="warning.main">
                No teacher assigned
              </Typography>
            )}
          </SummaryCard>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <SummaryCard title="Risk">
            <Box sx={{ mb: 1 }}>
              <OutlinedBadge
                label={risk.level === 'none' ? 'No issues' : `${risk.flags.length} flag(s)`}
                color={riskChipColor(risk.level)}
                size="small"
              />
            </Box>
            {risk.flags.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Account looks healthy.
              </Typography>
            ) : (
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {risk.flags.map((f) => (
                  <OutlinedBadge
                    key={f.code}
                    label={f.label}
                    size="small"
                    color={
                      f.severity === 'error'
                        ? 'error'
                        : f.severity === 'warning'
                          ? 'warning'
                          : 'default'
                    }
                  />
                ))}
              </Box>
            )}
          </SummaryCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserOverviewPanel;
